import { Link } from '../router';
import { availabilityLabel, availabilityTone, products } from '../content';

/**
 * §4.1 Products: CRM, SRM and Inventory, the HR modules, Reports, API and the AI add-on.
 *
 * Every card is rendered from the shared product list, availability included, so this page
 * cannot drift from what the platform's own catalogue says is sellable.
 */
export default function Products({ navigate }: { navigate: (to: string) => void }) {
  return (
    <>
      <section className="section">
        <div className="wrap">
          <div className="section__head">
            <span className="eyebrow">Applications</span>
            <h1 style={{ marginBottom: 16 }}>What ClientMa runs</h1>
            <p>
              Each application carries its own subscription, its own seats and its own
              entitlements. What is available today says so; what is planned says that instead.
            </p>
          </div>

          <div className="grid" style={{ gap: 20 }}>
            {products.map((product) => (
              <article key={product.key} className="card">
                <div
                  className="grid grid--2"
                  style={{ gap: 32, alignItems: 'start', gridTemplateColumns: '1fr 1.2fr' }}
                >
                  <div>
                    {availabilityLabel(product.availability) && (
                      <span className={`pill pill--${availabilityTone(product.availability)}`}>
                        {availabilityLabel(product.availability)}
                      </span>
                    )}

                    <h2
                      style={{
                        fontSize: '1.5rem',
                        margin: availabilityLabel(product.availability)
                          ? '14px 0 10px'
                          : '0 0 10px',
                      }}
                    >
                      {product.name}
                    </h2>
                    <p>{product.summary}</p>

                    <p style={{ marginTop: 14, fontSize: '0.86rem', color: 'var(--muted)' }}>
                      <strong style={{ color: 'var(--heading)' }}>Commercial treatment:</strong>{' '}
                      {product.commercial}
                    </p>
                  </div>

                  <ul className="ticks">
                    {product.capabilities.map((capability) => (
                      <li key={capability}>{capability}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--sunken">
        <div className="wrap">
          <div className="section__head">
            <span className="eyebrow">Entitlements</span>
            <h2>What a subscription actually controls</h2>
            <p>
              Six checks decide whether somebody can do something, and each is enforced on its
              own. Buying more seats does not quietly grant a permission, and switching a product
              on does not grant a feature the plan left out.
            </p>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Check</th>
                  <th>Example</th>
                  <th>What enforces it</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Product subscription</td>
                  <td>Inventory Professional, 25 seats, renewing 1 April</td>
                  <td>
                    Tenant activation with effective dates. A suspended or cancelled tenant loses
                    the product even while the row is there; a past-due one keeps working through
                    the grace period.
                  </td>
                </tr>
                <tr>
                  <td>Seat assignment</td>
                  <td>18 of 25 people assigned Inventory; the other 7 have CRM only</td>
                  <td>
                    A licence record per person per product. Nothing is shared between products,
                    so an SRM buyer does not silently hold an Inventory seat.
                  </td>
                </tr>
                <tr>
                  <td>Plan feature</td>
                  <td>Sourcing events on SRM Professional, not on Starter</td>
                  <td>
                    A materialised entitlement row per feature, checked in the service. The
                    product refuses the action, not just the menu item — a feature hidden from
                    the screen and reachable from the API is not a plan boundary.
                  </td>
                </tr>
                <tr>
                  <td>Role permission</td>
                  <td>A storekeeper posts a receipt; only a controller writes stock off</td>
                  <td>
                    Role and profile permissions, checked in the service before anything is read
                    or written.
                  </td>
                </tr>
                <tr>
                  <td>Record visibility</td>
                  <td>The Johor warehouse sees Johor stock; a supplier sees only their own orders</td>
                  <td>
                    Company, department, hierarchy, team and explicit shares — and, for suppliers,
                    a separate token audience with no route that takes another supplier&rsquo;s id.
                  </td>
                </tr>
                <tr>
                  <td>Metered allowance</td>
                  <td>Monthly AI credits, set by plan and toppable per customer</td>
                  <td>
                    A wallet with a ledger behind it. Credits are held before a request and
                    settled at what it actually used; a request that produced no answer gives its
                    hold back.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: 28 }}>
            <Link to="/pricing" onNavigate={navigate}>
              See what each plan includes →
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}
